import { useCallback, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { scrollToIdWithHeaderOffset } from "@/lib/scroll";
import logoEureka from "@/assets/logo_eureka.svg";

const ENTERPRISE_PAGES = [
  "/choisir-eureka-entreprise",
  "/solution-inclusion",
  "/solution-handicap",
  "/solution-expert",
  "/conseil-strategie-rh",
];

const CANDIDATE_PAGES = [
  "/choisir-eureka",
  "/parcours-impulsion",
  "/parcours-handicap",
  "/parcours-carriere-progression",
  "/postuler-sans-cv",
  "/cv-builder",
];

const getAudienceFromPath = (pathname: string): "candidate" | "company" => {
  // IMPORTANT: check enterprise pages first because `/choisir-eureka-entreprise`
  // starts with `/choisir-eureka`.
  if (ENTERPRISE_PAGES.some((p) => pathname.startsWith(p))) return "company";
  return CANDIDATE_PAGES.some((p) => pathname.startsWith(p)) ? "candidate" : "company";
};

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const audience = getAudienceFromPath(location.pathname);

  const CONTACT_FORM_HASH = "contact-form";
  const contactFormHref = `/contact?audience=${audience}#${CONTACT_FORM_HASH}`;
  const recruitHref = `/contact?audience=company#${CONTACT_FORM_HASH}`;
  const agenciesHref = `/contact?audience=${audience}#carte-agences`;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [candidatOpen, setCandidatOpen] = useState(false);
  const [entrepriseOpen, setEntrepriseOpen] = useState(false);

  const scrollToContactFormIfOnPage = useCallback(
    (e: React.MouseEvent) => {
      if (location.pathname !== "/contact") return;

      e.preventDefault();

      // Keep URL hash consistent (even if the user is already on the same hash)
      if (location.hash !== `#${CONTACT_FORM_HASH}`) {
        navigate(
          {
            pathname: "/contact",
            search: location.search,
            hash: `#${CONTACT_FORM_HASH}`,
          },
          { replace: true },
        );
      }

      // Always scroll (allows repeated clicks after manual scrolling)
      scrollToIdWithHeaderOffset(CONTACT_FORM_HASH, { behavior: "smooth", extraOffsetPx: 8 });
    },
    [CONTACT_FORM_HASH, location.hash, location.pathname, location.search, navigate],
  );

  return (
    <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b shadow-sm">
      <div className="container flex h-20 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2">
          <img src={logoEureka} alt="Réseau Eureka" className="h-12 w-auto" width={163} height={48} />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6">
          <NavigationMenu>
            <NavigationMenuList>
              {/* Candidat Menu */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent hover:bg-primary/10 hover:text-primary">
                  Candidat
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/choisir-eureka"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary"
                        >
                          <div className="text-sm font-medium leading-none">Choisir Eureka</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            Découvrez notre réseau
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/parcours-impulsion"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary"
                        >
                          <div className="text-sm font-medium leading-none">Parcours Impulsion</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Rebondir et construire</p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/parcours-handicap"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary"
                        >
                          <div className="text-sm font-medium leading-none">Parcours Handicap</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Emploi accessible</p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/parcours-carriere-progression"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary"
                        >
                          <div className="text-sm font-medium leading-none">Carrière & Progression</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Profils qualifiés</p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li className="md:col-span-2">
                      <div className="grid w-full grid-cols-2 gap-3">
                        <NavigationMenuLink asChild>
                          <Link
                            to="/postuler-sans-cv"
                            className="flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors px-6 py-3"
                          >
                            <div className="text-sm font-medium">Postulez sans CV !</div>
                          </Link>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/cv-builder"
                            className="flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors px-6 py-3"
                          >
                            <div className="text-sm font-medium">Générez votre CV !</div>
                          </Link>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                          <a
                            href="https://emplois.reseau-eureka.fr/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="col-span-2 flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors px-6 py-3"
                          >
                            <div className="text-sm font-medium">Nos offres d'emploi !</div>
                          </a>
                        </NavigationMenuLink>
                      </div>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Entreprise Menu */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent hover:bg-primary/10 hover:text-primary">
                  Entreprise
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/choisir-eureka-entreprise"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary"
                        >
                          <div className="text-sm font-medium leading-none">Choisir Eureka</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            Découvrez notre accompagnement
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/solution-inclusion"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary"
                        >
                          <div className="text-sm font-medium leading-none">Solution Inclusion</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            Recrutement inclusif et insertion
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/solution-handicap"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary"
                        >
                          <div className="text-sm font-medium leading-none">Solution Handicap</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Recrutement et OETH</p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/solution-expert"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary"
                        >
                          <div className="text-sm font-medium leading-none">Solution Expert</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Profils qualifiés et spécialisés</p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/conseil-strategie-rh"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary"
                        >
                          <div className="text-sm font-medium leading-none">Conseil Stratégie RH</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Accompagnement stratégique</p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li className="flex items-center">
                      <NavigationMenuLink asChild>
                        <Link
                          to={recruitHref}
                          className="flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors px-6 py-3"
                        >
                          <div className="text-sm font-medium">Je recrute</div>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <a
                  href="https://www.lavarappe.fr/actualites/ressources-competences"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                >
                  Actualités
                </a>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <Link
                  to={agenciesHref}
                  className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                >
                  Nos agences
                </Link>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <Button variant="default" asChild className="ml-4">
            <Link to={contactFormHref} onClick={scrollToContactFormIfOnPage} className="flex items-center gap-2">
              Contactez-nous
              <FileText className="h-4 w-4" />
            </Link>
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t bg-background max-h-[calc(100vh-5rem)] overflow-y-auto">
          <nav className="container py-4 space-y-1">
            {/* Candidat Section - Collapsible */}
            <Collapsible open={candidatOpen} onOpenChange={setCandidatOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-semibold text-foreground hover:text-primary">
                Candidat
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${candidatOpen ? "rotate-180" : ""}`}
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 pl-2">
                <Link
                  to="/choisir-eureka"
                  className="block py-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Choisir Eureka
                </Link>
                <Link
                  to="/parcours-impulsion"
                  className="block py-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Parcours Impulsion
                </Link>
                <Link
                  to="/parcours-handicap"
                  className="block py-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Parcours Handicap
                </Link>
                <Link
                  to="/parcours-carriere-progression"
                  className="block py-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Carrière & Progression
                </Link>
                <div className="flex flex-col gap-2 mt-2 mb-3">
                  <Button variant="default" size="sm" asChild className="w-full">
                    <Link to="/postuler-sans-cv" onClick={() => setMobileMenuOpen(false)}>
                      Postulez sans CV !
                    </Link>
                  </Button>
                  <Button variant="default" size="sm" asChild className="w-full">
                    <Link to="/cv-builder" onClick={() => setMobileMenuOpen(false)}>
                      Générez votre CV !
                    </Link>
                  </Button>
                  <Button variant="default" size="sm" asChild className="w-full">
                    <a href="https://emplois.reseau-eureka.fr/" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                      Nos offres d'emploi !
                    </a>
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Separator */}
            <div className="border-t border-border my-3" />

            {/* Entreprise Section - Collapsible */}
            <Collapsible open={entrepriseOpen} onOpenChange={setEntrepriseOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-semibold text-foreground hover:text-primary">
                Entreprise
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${entrepriseOpen ? "rotate-180" : ""}`}
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 pl-2">
                <Link
                  to="/choisir-eureka-entreprise"
                  className="block py-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Choisir Eureka
                </Link>
                <Link
                  to="/solution-inclusion"
                  className="block py-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Solution Inclusion
                </Link>
                <Link
                  to="/solution-handicap"
                  className="block py-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Solution Handicap
                </Link>
                <Link
                  to="/solution-expert"
                  className="block py-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Solution Expert
                </Link>
                <Link
                  to="/conseil-strategie-rh"
                  className="block py-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Conseil Stratégie RH
                </Link>
                <div className="mt-2 mb-3">
                  <Button variant="default" size="sm" asChild className="w-full">
                    <Link to={recruitHref} onClick={() => setMobileMenuOpen(false)}>
                      Je recrute
                    </Link>
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Separator */}
            <div className="border-t border-border my-3" />

            {/* Actualités */}
            <a
              href="https://www.lavarappe.fr/actualites/ressources-competences"
              target="_blank"
              rel="noopener noreferrer"
              className="block py-2 text-sm font-semibold text-foreground hover:text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              Actualités
            </a>

            {/* Nos agences */}
            <Link
              to={agenciesHref}
              className="block py-2 text-sm font-semibold text-foreground hover:text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              Nos agences
            </Link>

            {/* Contact Button */}
            <div className="pt-3 pb-2">
              <Button variant="default" asChild className="w-full">
                <Link
                  to={contactFormHref}
                  onClick={(e) => {
                    scrollToContactFormIfOnPage(e);
                    setMobileMenuOpen(false);
                  }}
                >
                  Contactez-nous
                </Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
